import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createNote,
    deleteNote,
    type NoteSeverity,
    type NoteVisibility,
    type PatientNote,
    updateNote
} from '../services/patientNotes';
import type { TiptapDoc } from '@odo/shared-ui';
import { useToast } from '../providers/ToastProvider';
import { patientNotesQuery } from './patientNotesQuery';
import { PATIENT_NOTES_KEY } from './queryKeys';

export function usePatientNotes(patientId: string | undefined) {
    const { data, isLoading, error } = useQuery({
        ...patientNotesQuery(patientId ?? ''),
        enabled: !!patientId,
        staleTime: 5 * 60 * 1000
    });

    const allNotes = data ?? [];
    // Single query (includeArchived=true) - partition client-side by archivedAt,
    // no re-sort, so consumers of `notes` keep seeing active-only in canonical order.
    const notes = allNotes.filter((n) => !n.archivedAt);
    const archivedNotes = allNotes.filter((n) => n.archivedAt);

    return { notes, archivedNotes, isLoading, error };
}

interface CreateNoteBody {
    severity: NoteSeverity;
    title: string;
    bodyDoc?: TiptapDoc;
    body?: string;
    visibility?: NoteVisibility;
    pinned?: boolean;
}

export function useCreateNote(patientId: string) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (body: CreateNoteBody) => createNote(patientId, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PATIENT_NOTES_KEY(patientId) });
            showToast('Note added', 'success');
        },
        onError: () => {
            showToast('Failed to add note', 'error');
        }
    });
}

interface UpdateNoteBody {
    severity?: NoteSeverity;
    title?: string;
    bodyDoc?: TiptapDoc;
    body?: string;
    visibility?: NoteVisibility;
    pinned?: boolean;
}

export function useUpdateNote(patientId: string) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: ({ noteId, body }: { noteId: string; body: UpdateNoteBody }) =>
            updateNote(patientId, noteId, body),
        onMutate: async ({ noteId, body }) => {
            // Only apply optimistic update for a pure pin toggle (no other fields).
            const isPinToggle = 'pinned' in body && Object.keys(body).length === 1;
            if (!isPinToggle) return undefined;

            await queryClient.cancelQueries({ queryKey: PATIENT_NOTES_KEY(patientId) });
            const previous = queryClient.getQueryData<PatientNote[]>(PATIENT_NOTES_KEY(patientId));

            if (previous) {
                queryClient.setQueryData<PatientNote[]>(
                    PATIENT_NOTES_KEY(patientId),
                    previous.map((n) => (n.id === noteId ? { ...n, pinned: body.pinned! } : n))
                );
            }

            return { previous };
        },
        onError: (_err, _vars, context) => {
            if (context?.previous) {
                queryClient.setQueryData(PATIENT_NOTES_KEY(patientId), context.previous);
            }
            showToast('Failed to update note', 'error');
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: PATIENT_NOTES_KEY(patientId) });
        }
    });
}

export function useArchiveNote(patientId: string) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: ({ noteId, archived }: { noteId: string; archived: boolean }) =>
            updateNote(patientId, noteId, { archived }),
        // Optimistic archivedAt flip (same pattern as the pin toggle) so the note
        // moves between the active/archived lists instantly.
        onMutate: async ({ noteId, archived }) => {
            await queryClient.cancelQueries({ queryKey: PATIENT_NOTES_KEY(patientId) });
            const previous = queryClient.getQueryData<PatientNote[]>(PATIENT_NOTES_KEY(patientId));

            if (previous) {
                queryClient.setQueryData<PatientNote[]>(
                    PATIENT_NOTES_KEY(patientId),
                    previous.map((n) =>
                        n.id === noteId
                            ? { ...n, archivedAt: archived ? new Date().toISOString() : null }
                            : n
                    )
                );
            }

            return { previous };
        },
        onSuccess: (_data, { archived }) => {
            showToast(archived ? 'Note archived' : 'Note unarchived', 'success');
        },
        onError: (_err, { archived }, context) => {
            if (context?.previous) {
                queryClient.setQueryData(PATIENT_NOTES_KEY(patientId), context.previous);
            }
            showToast(archived ? 'Failed to archive note' : 'Failed to unarchive note', 'error');
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: PATIENT_NOTES_KEY(patientId) });
        }
    });
}

export function useDeleteNote(patientId: string) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (noteId: string) => deleteNote(patientId, noteId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PATIENT_NOTES_KEY(patientId) });
            showToast('Note deleted', 'success');
        },
        onError: () => {
            showToast('Failed to delete note', 'error');
        }
    });
}
